import { Editor, Node, Path, Range, Text, Transforms } from 'slate'
import hotkeys from 'slate-hotkeys'

import { BASE_BLOCK_ELEMENT_TYPES, LIST_TYPES } from './serialization'
import { getBaseBlockElementType } from './base_block_elements'

function getLastOfIterable(iterable) {
  const array = Array.from(iterable)
  return array[array.length - 1]
}

export const isListNode = (node) => LIST_TYPES.includes(node.type)

export function isAnyListSelected(editor) {
  const nodes = Array.from(Editor.nodes(editor, { match: isListNode }))

  return nodes.length > 0
}

export function getList(editor, format) {
  const listEntry = getLastOfIterable(Editor.nodes(editor, { match: (n) => LIST_TYPES.includes(n.type) }))
  if (!listEntry || (format && listEntry[0].type !== format)) return { isList: false }
  const [list, listPath] = listEntry

  const listItemEntry = getLastOfIterable(Editor.nodes(editor, { match: (n) => n.type === 'list-item' }))
  if (!listItemEntry) return { isList: false }
  const [listItem, listItemPath] = listItemEntry
  const listItemIndex = listItemPath[listItemPath.length - 1]

  const listItemChildEntry = getLastOfIterable(Editor.nodes(editor, { match: (n) => n.type === 'list-item-child' }))
  if (!listItemChildEntry) return { isList: false }
  const [listItemChild, listItemChildPath] = listItemChildEntry

  return { list, listPath, listItem, listItemPath, listItemChild, listItemChildPath, listItemIndex, isList: true }
}

export function withLists(editor) {
  const { normalizeNode } = editor

  editor.normalizeNode = (entry) => {
    const [node, path] = entry

    // Merge lists together if they directly follow each other.
    if (Editor.isEditor(node)) {
      for (let i = 0; i < node.children.length; i++) {
        const child = node.children[i]
        const nextChild = node.children[i + 1]
        if (LIST_TYPES.includes(child.type) && nextChild && child.type === nextChild.type) {
          Transforms.mergeNodes(editor, { at: [i + 1] })
          return
        }
      }
    }

    // Filter out invalid children from list items
    if (node.type === 'list-item') {
      node.children.forEach((n, index) => {
        const isInvalidChild = ![...LIST_TYPES, 'list-item-child'].includes(n.type)

        if (isInvalidChild) {
          Transforms.setNodes(editor, { type: 'list-item-child' }, { at: [...path, index] })
        }
      })
    }

    normalizeNode(entry)
  }

  return editor
}

function unnestListItem(editor) {
  const { listItemPath, list, listItemIndex, listPath, listItem } = getList(editor)
  const isNestedItem = Node.parent(editor, listPath).type === 'list-item'
  if (isNestedItem) {
    Transforms.moveNodes(editor, { at: listItemPath, to: Path.next(Path.parent(listPath)) })
    const childrenToTakeOver = list.children.slice(listItemIndex + 1)
    if (childrenToTakeOver.length > 0) {
      Transforms.insertNodes(
        editor,
        { type: list.type, children: childrenToTakeOver },
        { at: [...Path.next(Path.parent(listPath)), 1] },
      )
      childrenToTakeOver.forEach(() => Transforms.removeNodes(editor, { at: listItemPath }))
    }
    if (listItemIndex === 0) Transforms.removeNodes(editor, { at: listPath })
  } else {
    const hasList = listItem.children.length > 1
    if (hasList) Transforms.removeNodes(editor, { at: [...listItemPath, 1] })
    Transforms.unwrapNodes(editor, { at: listItemPath })
    Transforms.setNodes(editor, { type: 'paragraph' }, { at: listItemPath })
    let nextPath = Path.next(listPath)
    Transforms.moveNodes(editor, { at: listItemPath, to: nextPath })
    nextPath = Path.next(nextPath)
    const childrenToTakeOver = list.children.slice(listItemIndex + 1)
    if (hasList) {
      Transforms.insertNodes(
        editor,
        { type: listItem.children[1].type, children: listItem.children[1].children },
        { at: nextPath },
      )
      nextPath = Path.next(nextPath)
    }
    if (childrenToTakeOver.length > 0) {
      Transforms.insertNodes(editor, { type: list.type, children: childrenToTakeOver }, { at: nextPath })
      nextPath = Path.next(nextPath)
      childrenToTakeOver.forEach(() => Transforms.removeNodes(editor, { at: listItemPath }))
    }
    if (listItemIndex === 0) Transforms.removeNodes(editor, { at: listPath })
  }
}

export function handleLineBreak(editor) {
  const { listItemChildPath, listItemPath, listItem, listItemChild } = getList(editor)
  const [[textNode]] = Editor.nodes(editor, { match: Text.isText })
  const emptyListItem = {
    type: 'list-item',
    children: [{ ...listItemChild, type: 'list-item-child', children: [{ ...textNode, text: '' }] }],
  }
  const isEmptyListItem = listItemChild.children.every((child) => child.text === '')
  if (isEmptyListItem) {
    unnestListItem(editor)
  } else if (Editor.isEnd(editor, editor.selection.focus, listItemChildPath)) {
    const nestedList = listItem.children[1]
    if (nestedList) {
      emptyListItem.children.push(nestedList)
      Transforms.removeNodes(editor, { at: [...listItemPath, 1] })
    }
    Transforms.insertNodes(editor, emptyListItem, { at: Path.next(listItemPath) })
    Transforms.select(editor, { path: [...Path.next(listItemPath), 0, 0], offset: 0 })
  } else if (Editor.isStart(editor, editor.selection.focus, listItemChildPath)) {
    Transforms.insertNodes(editor, emptyListItem, { at: Path.next(listItemPath) })
    Transforms.select(editor, { path: [...Path.next(listItemPath), 0, 0], offset: 0 })
  } else {
    Transforms.splitNodes(editor)
    Transforms.wrapNodes(editor, { type: 'list-item' })
    Transforms.moveNodes(editor, {
      at: [...listItemPath, 1],
      to: Path.next(listItemPath),
    })
  }
}

export function listEventHandler(editor, event) {
  if (hotkeys.isDeleteBackward(event)) {
    const { isList, listItemChildPath, listPath } = getList(editor)
    if (isList) {
      const [start, end] = Range.edges(editor.selection)
      const startOfLineSelected =
        Editor.isStart(editor, start, listItemChildPath) && Range.isCollapsed(editor.selection)

      // Only the line break of the previous line is selected.
      const lineBreakSelected =
        listPath[0] !== 0 &&
        Editor.isEnd(editor, start, Path.previous(listPath)) &&
        Editor.isStart(editor, end, listItemChildPath)

      // Unnest list item when the cursor is at the start position of it, or only preceded by a line break.
      if (startOfLineSelected || lineBreakSelected) {
        event.preventDefault()
        unnestListItem(editor)
      }
    }
  }
  // Unnest following list element when deleting forward at the end of line.
  else if (hotkeys.isDeleteForward(event)) {
    if (Range.isExpanded(editor.selection)) return

    const selectedPoint = Range.end(editor.selection)
    const rowIndex = selectedPoint.path[0]
    const hasNextLine = rowIndex !== editor.children.length - 1

    if (!hasNextLine) return

    const [nextLine] = Editor.node(editor, [rowIndex + 1])
    const nextLineIsList = LIST_TYPES.includes(nextLine.type)
    const endOfLineSelected = Editor.isEnd(editor, selectedPoint, [rowIndex])

    if (endOfLineSelected && nextLineIsList) {
      event.preventDefault()
      Transforms.move(editor)
      unnestListItem(editor)
      Transforms.move(editor, { reverse: true })
    }
  } else if (hotkeys.isSplitBlock(event) && Range.isCollapsed(editor.selection)) {
    const { isList } = getList(editor)
    if (!isList) return
    event.preventDefault()
    handleLineBreak(editor)
  } else if (event.key === 'Tab') {
    const { isList, listItemIndex, listItemPath, list } = getList(editor)
    if (isList) {
      event.preventDefault()
      if (event.nativeEvent.shiftKey) {
        unnestListItem(editor)
      } else {
        if (listItemIndex === 0) return
        const prevListItem = list.children[listItemIndex - 1]
        const prevListItemPath = Path.previous(listItemPath)
        if (prevListItem.children.length === 1) {
          Transforms.wrapNodes(editor, { type: list.type }, { at: listItemPath })
          Transforms.moveNodes(editor, { at: listItemPath, to: [...prevListItemPath, prevListItem.children.length] })
        } else {
          Transforms.moveNodes(editor, {
            at: listItemPath,
            to: [...prevListItemPath, 1, prevListItem.children[1].children.length],
          })
        }
      }
    }
  }
}

export function toggleList(editor, format) {
  const { isList } = getList(editor, format)
  const { isList: isAnyList, listPath: anyListPath } = getList(editor)

  const isDifferentList = !isList && isAnyList

  if (isList) {
    while (getList(editor).isList) unnestListItem(editor)
  } else if (isDifferentList) {
    Transforms.setNodes(editor, { type: format }, { at: anyListPath })
  } else {
    // Convert all selected base block elements into lists.
    const hasBaseBlockElement = () => Boolean(getBaseBlockElementType(editor, editor.selection))
    while (hasBaseBlockElement()) {
      const [[, path]] = Editor.nodes(editor, { match: (n) => BASE_BLOCK_ELEMENT_TYPES.includes(n.type) })
      Transforms.setNodes(editor, { type: 'list-item-child' }, { at: path })
      Transforms.wrapNodes(editor, { type: 'list-item' }, { at: path })
      Transforms.wrapNodes(editor, { type: format }, { at: path })
    }
  }
}
